/* oxlint-disable no-restricted-imports -- the theme is defined against Mantine's own types */
import type { CSSVariablesResolver } from '@mantine/core';

/**
 * Scheme-specific redefinitions of Mantine's semantic color tokens (EPD-1186;
 * dark scheme added in the dark-mode project).
 *
 * Values must stay `var(--mantine-color-*)` references — never hex — so the
 * branded-community `primary` palette override keeps flowing through the
 * semantic layer.
 *
 * The full raw→semantic mapping (and the rationale for each redefinition)
 * lives in tools/theme/SEMANTIC-TOKENS.md; css-variables-resolver.test.ts
 * asserts the emitted hex of every token listed there.
 */
export const semanticColorOverridesLight: Record<string, string> = {
  '--mantine-color-text': 'var(--mantine-color-gray-9)',
  '--mantine-color-dimmed': 'var(--mantine-color-gray-7)',
  // red-5, not the majority red-4: #E83535 is ~4.2:1 on white and fails WCAG AA for
  // normal-size error text; #E20303 passes (~5.9:1).
  '--mantine-color-error': 'var(--mantine-color-red-5)',
  '--mantine-color-success': 'var(--mantine-color-green-5)',
  '--mantine-color-default-border': 'var(--mantine-color-gray-2)',
  // Form fields draw a shade darker than the app-wide border (EPD-1390): the Project Builder
  // design specifies gray-3, and unlike a card edge an input's border is the only thing marking
  // out the control, so it earns its own token rather than folding into `-default-border`.
  // Mantine has no native equivalent — it hardcodes gray-4 in the `default` input variant.
  '--mantine-color-input-border': 'var(--mantine-color-gray-3)',
  // gray-3 on the native disabled bg (gray-2) is ~1.2:1 and unreadable for QC; gray-5 restores
  // Mantine's native disabled text while staying visibly muted vs primary text (gray-9).
  '--mantine-color-disabled-color': 'var(--mantine-color-gray-5)',
  '--mantine-color-disabled-border': 'var(--mantine-color-gray-3)',
  '--mantine-color-primary-light': 'var(--mantine-color-primary-0)',
  '--mantine-color-primary-light-hover': 'var(--mantine-color-primary-1)',
  '--mantine-color-gray-light': 'var(--mantine-color-gray-0)',
  '--mantine-color-gray-light-hover': 'var(--mantine-color-gray-1)',
  '--mantine-color-red-light': 'var(--mantine-color-red-0)',
  '--mantine-color-red-light-hover': 'var(--mantine-color-red-1)',
  '--mantine-color-yellow-light': 'var(--mantine-color-yellow-0)',
  '--mantine-color-yellow-light-hover': 'var(--mantine-color-yellow-1)',
  // Green was the one family left on Mantine's native shade-1/2 tints, which made a green status
  // surface sit a step darker than the amber one beside it (EPD-1426's Draft/Ready pill).
  '--mantine-color-green-light': 'var(--mantine-color-green-0)',
  '--mantine-color-green-light-hover': 'var(--mantine-color-green-1)',
  // One shade darker than the other `-light` tints: the Project Builder draws every info callout
  // on blue-1 (#E0F4F4), not the blue-0 the rest of the scale would imply, so the tint and its
  // hover both step up to keep that fill and still leave a visible hover.
  '--mantine-color-blue-light': 'var(--mantine-color-blue-1)',
  '--mantine-color-blue-light-hover': 'var(--mantine-color-blue-2)',
  '--mantine-color-blue-light-color': 'var(--mantine-color-blue-7)',
};

/**
 * Dark-scheme redefinitions (dark-mode project).
 *
 * The brand `dark` palette (tools/colors) is what Mantine paints most dark
 * surfaces from, so these overrides mostly fine-tune on top of it: body/cards
 * sit one step above the page floor (gray-light), border/dimmed are nudged for
 * contrast, `-anchor`/`-*-light-color` flip to light foregrounds so links and
 * text-on-tint stay readable on near-black, the `-*-light` tints drop to deep
 * shade-9/8 fills, and `-error`/`-success` brighten for dark backgrounds.
 */
export const semanticColorOverridesDark: Record<string, string> = {
  // Cards/surfaces sit one step ABOVE the page floor (gray-light, below), so
  // cards read as elevated — mirroring light, where the white body is lighter
  // than the gray-light page.
  '--mantine-color-body': 'var(--mantine-color-dark-6)',
  '--mantine-color-default': 'var(--mantine-color-dark-6)',
  '--mantine-color-default-hover': 'var(--mantine-color-dark-5)',
  '--mantine-color-default-color': 'var(--mantine-color-dark-0)',
  // dark-3 (~3:1 on the body) for perceivable control borders; dark-4 was ~2:1 (WCAG 1.4.11).
  '--mantine-color-default-border': 'var(--mantine-color-dark-3)',
  // Dark has no equivalent of light's one-shade-darker field edge — the dark scale is coarser and
  // dark-3 is already the perceivable-border floor, so inputs share it. Still an improvement on
  // Mantine's stock dark-4 input border (~1.8:1).
  '--mantine-color-input-border': 'var(--mantine-color-dark-3)',
  '--mantine-color-text': 'var(--mantine-color-dark-0)',
  '--mantine-color-dimmed': 'var(--mantine-color-dark-1)',
  '--mantine-color-placeholder': 'var(--mantine-color-dark-2)',
  '--mantine-color-disabled-color': 'var(--mantine-color-dark-3)',
  '--mantine-color-disabled-border': 'var(--mantine-color-dark-4)',
  // Links: primaryShade.dark (primary-5) is only ~3.4:1 on the dark body; the
  // lighter primary-3 restores readable anchor text.
  '--mantine-color-anchor': 'var(--mantine-color-primary-3)',
  // red-4 is only ~3.4:1 on the dark body; red-3 clears WCAG AA (~4.6:1) for error text.
  '--mantine-color-error': 'var(--mantine-color-red-3)',
  '--mantine-color-success': 'var(--mantine-color-green-4)',
  '--mantine-color-primary-light': 'var(--mantine-color-primary-9)',
  '--mantine-color-primary-light-hover': 'var(--mantine-color-primary-8)',
  '--mantine-color-primary-light-color': 'var(--mantine-color-primary-2)',
  // Page floor / subtle panels: darker than the card surface (body) above it.
  '--mantine-color-gray-light': 'var(--mantine-color-dark-7)',
  '--mantine-color-gray-light-hover': 'var(--mantine-color-dark-6)',
  '--mantine-color-red-light': 'var(--mantine-color-red-9)',
  '--mantine-color-red-light-hover': 'var(--mantine-color-red-8)',
  '--mantine-color-yellow-light': 'var(--mantine-color-yellow-9)',
  '--mantine-color-yellow-light-hover': 'var(--mantine-color-yellow-8)',
  '--mantine-color-green-light': 'var(--mantine-color-green-9)',
  '--mantine-color-green-light-hover': 'var(--mantine-color-green-8)',
  '--mantine-color-blue-light': 'var(--mantine-color-blue-9)',
  '--mantine-color-blue-light-hover': 'var(--mantine-color-blue-8)',
  '--mantine-color-blue-light-color': 'var(--mantine-color-blue-3)',
};

/**
 * Field metrics, for laying something out *beside* a field rather than inside one.
 *
 * Mantine keeps its input sizing (`--input-height-*`) on the inner `.mantine-Input-wrapper`
 * element, so nothing outside a field — not even the field's own `InputWrapper` root — can read
 * it, and a sibling button that wants to sit on the field's line has no way to ask how tall the
 * field is or how much room its label takes above it. These mirror both, once, next to the
 * `InputWrapper` entry in theme.tsx that sets `--input-label-size`: change the label size there
 * and the block height here is what needs rechecking.
 *
 * `-label-block` is the line box the wrapper's strut gives the label, not the label's own height —
 * the 12px label is `inline-block` with no line-height of its own, so the body's sets it. Measured
 * in the browser at 24px against a 18px label. `FieldRow` in core-ui-compositions is the consumer.
 *
 * The label block is not per-size: the `InputWrapper` entry pins `--input-label-size` to `xs` at
 * every field size, so only the field's own height changes between them.
 */
export const fieldMetricVariables: Record<string, string> = {
  '--hl-field-height-xs': 'calc(1.875rem * var(--mantine-scale))',
  '--hl-field-height-sm': 'calc(2.25rem * var(--mantine-scale))',
  '--hl-field-label-block': 'calc(1.5rem * var(--mantine-scale))',
};

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: fieldMetricVariables,
  light: semanticColorOverridesLight,
  dark: semanticColorOverridesDark,
});
